import { useEffect, useState } from "react";
import InlineSVG from "react-inlinesvg";
import styled from "styled-components/macro";

import { useRef } from "react";
import DotsActionMenu from "../DotsActionMenu";
import Text from "../Text";
import { useToast } from "../ToastWrapper";
import { CopyableTextInput } from "./CopyableTextInput";

export function ShareLink({
  _author,
  documentId,
  onUnpublishLink,
  projectDocuments,
  _publishedAt,
  _shortId,
  updatedAt,
  url,
  portalRef,
  textIndent = true,
  onToggleIndent,
}) {
  const [title, setTitle] = useState(null);
  const [isUpdatingIndent, setIsUpdatingIndent] = useState(false);
  const { setToast } = useToast();

  const ref = useRef(null);

  // Note on optimistic update: if we remove the node, React
  // will throw an error when it later tries to remove the node
  // that doesn't exist. So instead, we hide the node.
  function handleUnpublish() {
    onUnpublishLink();
    // set a toast here
    setToast(
      <div className='flex items-center gap-2'>
        <InlineSVG
          src="/assets/check-simple.svg"
          className="success"
        />
        {title} successfully unpublished.
      </div>
    );

    ref.current.style.display = "none";
  }

  useEffect(() => {
    setTitle(projectDocuments.find((doc) => doc.id === documentId)?.title);
  }, [projectDocuments, documentId]);

  const handleToggleIndent = async () => {
    if (!onToggleIndent || isUpdatingIndent) {
      return;
    }

    const nextValue = !textIndent;
    setIsUpdatingIndent(true);
    try {
      await onToggleIndent(nextValue);
      setToast(
        <div className='flex items-center gap-2'>
          <InlineSVG
            src="/assets/check-simple.svg"
            className="success"
          />
          {`${title ?? "Document"} paragraph indent ${nextValue ? "enabled" : "disabled"}.`}
        </div>
      );
    } catch (_e) {
      setToast(
        <div className='flex items-center gap-2'>
          <InlineSVG
            src="/assets/close.svg"
            className="failed"
          />
          Could not update paragraph indent. Please try again.
        </div>
      );
    } finally {
      setIsUpdatingIndent(false);
    }
  };

  return (
    <Border ref={ref}>
      <Row>
        <ShareLinkClickable
          href={url}
          target="_blank"
        >
          <Text
            as="span"
            truncate
          >
            {title}
          </Text>
          <PublishedDate> Shared: {updatedAt.toLocaleDateString(undefined, { dateStyle: "medium" })}</PublishedDate>
        </ShareLinkClickable>
        <Actions>
          <CopyableTextInput
            type="url"
            value={url}
          />
          <ShareLinkActions
            onUnpublishLink={handleUnpublish}
            lastPublishedDate={updatedAt}
            portalRef={portalRef}
            textIndent={textIndent}
            onToggleIndent={handleToggleIndent}
          />
        </Actions>
      </Row>
    </Border>
  );
}

const ShareLinkClickable = styled.a`
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: baseline;
  color: var(--grey-0);

  > :first-child {
    margin-right: auto;
    flex: 1;
  }
`;

function ShareLinkActions(props) {
  return (
    <DotsActionMenu
      position="bottom-end"
      actions={{
        toggleIndent: {
          icon: null,
          onClick: () => props.onToggleIndent?.(),
          label: props.textIndent ? "Disable paragraph indent" : "Enable paragraph indent",
        },
        unpublish: {
          icon: (
            <InlineSVG
              src="/assets/delete.svg"
              aria-hidden="true"
            />
          ),
          destructive: true,
          onClick: props.onUnpublishLink,
          label: "Unpublish Link",
        },
      }}
      portalRef={props.portalRef}
    />
  );
}

const Border = styled.div`
  border: solid 1px var(--grey-3);
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  height: 60px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  grid-gap: 10px;
  align-items: center;
  position: relative;
  justify-content: space-between;

  button {
    margin-left: auto;
    font-size: 14px;
  }
`;

const PublishedDate = styled.span`
  color: var(--grey-1);
`;

const Actions = styled.div`
  display: flex;
  align-self: flex-start;
  align-items: baseline;
`;
